import clsx from 'clsx';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Merge CSS class names (clsx helper without tailwind-merge since we use Bootstrap)
 */
export const cn = (...inputs) => clsx(...inputs);

/**
 * Format a monetary amount with the correct currency symbol & decimals.
 * Falls back to INR if no code/symbol provided.
 */
export const formatCurrency = (amount, currencyCode = 'INR', currencySymbol) => {
    const num = Number(amount);
    if (Number.isNaN(num)) return '—';

    if (currencySymbol) {
        return `${currencySymbol}${num.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    }

    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
        }).format(num);
    } catch {
        return `${currencyCode} ${num.toFixed(2)}`;
    }
};

/**
 * Format an ISO date string to a human-readable date.
 */
export const formatDate = (dateStr, fmt = 'MMM dd, yyyy HH:mm') => {
    if (!dateStr) return '—';
    try {
        const d = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
        return format(d, fmt);
    } catch {
        return dateStr;
    }
};

/**
 * Relative time for recent items (e.g. "2 hours ago").
 */
export const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
        return formatDistanceToNow(d, { addSuffix: true });
    } catch {
        return dateStr;
    }
};

/**
 * Mask an API key for display: show first 6 and last 4 chars.
 */
export const maskApiKey = (key) => {
    if (!key || key.length < 12) return key || '';
    return `${key.slice(0, 6)}${'•'.repeat(key.length - 10)}${key.slice(-4)}`;
};

/**
 * Copy text to clipboard.
 */
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
};

/**
 * Build query-string from an object, omitting null/undefined/"" values.
 */
export const buildQueryString = (params) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') {
            sp.append(k, v);
        }
    });
    const qs = sp.toString();
    return qs ? `?${qs}` : '';
};

/**
 * Export an array of objects as a CSV download.
 */
export const exportToCsv = (rows, filename = 'export.csv') => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
            headers
                .map((h) => {
                    const cell = row[h] ?? '';
                    const str = String(cell).replace(/"/g, '""');
                    return `"${str}"`;
                })
                .join(',')
        ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};
